import { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import { collection, onSnapshot, query, orderBy, limit, where, addDoc, updateDoc, doc, serverTimestamp, getDocs } from "firebase/firestore";

const NOTIF_TYPES = [
  { id: "shift",     label: "Shift Alert",    color: "#C9A84C", bg: "#FFF8E7" },
  { id: "incident",  label: "Incident Alert", color: "#C0392B", bg: "#FEF2F2" },
  { id: "broadcast", label: "Broadcast",      color: "#1A1A2E", bg: "#F0F2F8" },
  { id: "emergency", label: "Emergency",      color: "#7C3AED", bg: "#F3F0FF" },
  { id: "system",    label: "System",         color: "#2E7D52", bg: "#F0FFF4" },
];

const fmtTime = (ts) => {
  if (!ts) return "";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return d.toLocaleDateString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" });
  } catch { return ""; }
};

const S = {
  card: { background:"#FFFFFF", border:"1px solid #E2DDD6", borderRadius:10, padding:16, marginBottom:12 },
  inp:  { background:"#FAFAF8", border:"1px solid #E2DDD6", color:"#1A1A2E", padding:"9px 12px", borderRadius:7, fontSize:13, width:"100%", boxSizing:"border-box" },
  sel:  { background:"#FAFAF8", border:"1px solid #E2DDD6", color:"#1A1A2E", padding:"9px 12px", borderRadius:7, fontSize:13, width:"100%" },
  btn:  (bg, col) => ({ background:bg, color:col||"#fff", border:"none", padding:"8px 18px", borderRadius:7, cursor:"pointer", fontSize:12, fontWeight:700 }),
  lbl:  { fontSize:10, color:"#9B8F7A", letterSpacing:1.5, display:"block", marginBottom:5, textTransform:"uppercase", fontWeight:700 },
};

export default function NotificationCentre() {
  const [notifications, setNotifications] = useState([]);
  const [officers, setOfficers]           = useState([]);
  const [tab, setTab]                     = useState("send");
  const [sending, setSending]             = useState(false);
  const [form, setForm] = useState({
    type: "broadcast", title: "", body: "", target: "all", officer_uid: "", site: "",
  });

  const tenantId = auth.currentUser?.tenantId || "osecurity-ltd";

  const [responses, setResponses] = useState([]);

  useEffect(() => {
    const q = query(collection(db, "notifications"), orderBy("sent_at", "desc"), limit(50));
    return onSnapshot(q, snap => setNotifications(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, []);

  useEffect(() => {
    const q = query(collection(db, "notification_responses"), orderBy("responded_at", "desc"), limit(30));
    return onSnapshot(q, snap => setResponses(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, []);

  const markResponseRead = async (id) => {
    await updateDoc(doc(db, "notification_responses", id), { read_by_admin: true });
  };

  const unreadResponses = responses.filter(r => !r.read_by_admin).length;

  useEffect(() => {
    const q = query(collection(db, "users"), where("tenant_id", "==", tenantId));
    return onSnapshot(q, snap => setOfficers(snap.docs.map(d => ({ uid:d.id, ...d.data() }))));
  }, [tenantId]);

  const sendNotification = async () => {
    if (!form.title || !form.body) { alert("Title and message required"); return; }
    setSending(true);
    try {
      // Save to Firestore notifications collection (guard app reads this)
      const targets = form.target === "all"
        ? officers.map(o => o.uid)
        : [form.officer_uid];

      for (const uid of targets) {
        const officer = officers.find(o => o.uid === uid);
        await addDoc(collection(db, "notifications"), {
          type:         form.type,
          title:        form.title,
          body:         form.body,
          site:         form.site || null,
          target_uid:   uid,
          target_name:  officer?.name || officer?.email || "Officer",
          sent_by:      auth.currentUser?.email || "admin",
          sent_at:      serverTimestamp(),
          read:         false,
          tenant_id:    tenantId,
        });
      }

      // If single officer has FCM token, send push directly
      if (form.target !== "all") {
        const officer = officers.find(o => o.uid === form.officer_uid);
        if (officer?.fcm_token) {
          // Cloud Function will handle FCM — notification doc creation triggers it
          console.log("Notification saved — FCM will be sent via Cloud Function");
        }
      }

      alert(`Notification sent to ${targets.length} officer${targets.length > 1 ? "s" : ""}`);
      setForm({ type:"broadcast", title:"", body:"", target:"all", officer_uid:"", site:"" });
      setTab("history");
    } catch(e) {
      alert("Failed: " + e.message);
    }
    setSending(false);
  };

  const markRead = async (id) => {
    await updateDoc(doc(db, "notifications", id), { read: true });
  };

  const unread = notifications.filter(n => !n.read).length;

  return (
    <div style={{ padding:"0 24px 24px", color:"#1A1A2E" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 0 16px" }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:"#1A1A2E" }}>Notification Centre</div>
          <div style={{ fontSize:11, color:"#9B8F7A", marginTop:2 }}>
            Send alerts to guard app · {unread} unread · {notifications.length} total
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {["send","history","responses","templates"].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              background: tab===t ? "#1A1A2E" : "#FFFFFF",
              color:       tab===t ? "#C9A84C"  : "#6B6055",
              border:"1px solid #E2DDD6", padding:"6px 16px", borderRadius:7,
              cursor:"pointer", fontSize:12, fontWeight:700, textTransform:"capitalize"
            }}>{t==="responses" ? `Responses${unreadResponses>0?" ("+unreadResponses+")":""}` : t}{t==="history" && unread>0 ? ` (${unread})` : ""}</button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:20 }}>
        {[
          ["Total Sent",  notifications.length,                      "#1A1A2E"],
          ["Unread",      unread,                                    "#C0392B"],
          ["Officers",    officers.length,                           "#C9A84C"],
          ["Today",       notifications.filter(n => { try { const d = n.sent_at?.toDate?.(); return d && d.toDateString()===new Date().toDateString(); } catch{return false;} }).length, "#2E7D52"],
          ["Responses",   responses.length, "#7C3AED"],
        ].map(([l,v,c]) => (
          <div key={l} style={{ ...S.card, marginBottom:0, borderLeft:`3px solid ${c}`, padding:"14px 16px" }}>
            <div style={{ fontSize:26, fontWeight:800, color:c }}>{v}</div>
            <div style={{ fontSize:10, color:"#9B8F7A", letterSpacing:1.5, textTransform:"uppercase", marginTop:3 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* SEND TAB */}
      {tab === "send" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:16 }}>
          <div style={S.card}>
            <div style={{ fontSize:13, fontWeight:700, color:"#1A1A2E", marginBottom:16 }}>Compose Notification</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
              <div>
                <label style={S.lbl}>Notification Type</label>
                <select style={S.sel} value={form.type} onChange={e => setForm(f=>({...f,type:e.target.value}))}>
                  {NOTIF_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.lbl}>Send To</label>
                <select style={S.sel} value={form.target} onChange={e => setForm(f=>({...f,target:e.target.value}))}>
                  <option value="all">All Officers</option>
                  <option value="single">Specific Officer</option>
                </select>
              </div>
            </div>
            {form.target === "single" && (
              <div style={{ marginBottom:12 }}>
                <label style={S.lbl}>Officer</label>
                <select style={S.sel} value={form.officer_uid} onChange={e => setForm(f=>({...f,officer_uid:e.target.value}))}>
                  <option value="">Select officer...</option>
                  {officers.map(o => <option key={o.uid} value={o.uid}>{o.name || o.email}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom:12 }}>
              <label style={S.lbl}>Title</label>
              <input style={S.inp} placeholder="e.g. Shift Reminder — Paddington Square" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))}/>
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={S.lbl}>Message</label>
              <textarea style={{ ...S.inp, minHeight:90, resize:"vertical" }} placeholder="Notification message..." value={form.body} onChange={e => setForm(f=>({...f,body:e.target.value}))}/>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={S.lbl}>Site (optional)</label>
              <input style={S.inp} placeholder="e.g. Paddington Square" value={form.site} onChange={e => setForm(f=>({...f,site:e.target.value}))}/>
            </div>
            {/* Preview */}
            {form.title && (
              <div style={{ marginBottom:16 }}>
                <label style={S.lbl}>Preview</label>
                <div style={{
                  background: NOTIF_TYPES.find(t=>t.id===form.type)?.bg || "#F8F7F4",
                  border:`1px solid ${NOTIF_TYPES.find(t=>t.id===form.type)?.color || "#E2DDD6"}44`,
                  borderLeft:`3px solid ${NOTIF_TYPES.find(t=>t.id===form.type)?.color || "#C9A84C"}`,
                  borderRadius:8, padding:14
                }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#1A1A2E" }}>{form.title}</div>
                  {form.body && <div style={{ fontSize:12, color:"#6B6055", marginTop:4 }}>{form.body}</div>}
                  {form.site && <div style={{ fontSize:11, color:"#9B8F7A", marginTop:4 }}>Site: {form.site}</div>}
                </div>
              </div>
            )}
            <button style={{ ...S.btn("#C9A84C","#1A1A2E"), width:"100%", padding:12, opacity:sending?0.6:1 }} onClick={sendNotification} disabled={sending}>
              {sending ? "Sending..." : `Send to ${form.target==="all" ? `All ${officers.length} Officers` : "Officer"}`}
            </button>
          </div>

          {/* Quick actions sidebar */}
          <div>
            <div style={{ ...S.card, marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#1A1A2E", marginBottom:12 }}>Quick Send</div>
              {[
                { label:"Shift Reminder — Today", type:"shift", title:"Shift Reminder", body:"You have a shift starting soon. Please ensure you are on site and checked in." },
                { label:"Site Check Required", type:"system", title:"Site Inspection Required", body:"Please conduct a full site inspection and submit your patrol report." },
                { label:"Emergency Muster", type:"emergency", title:"Emergency Muster Point", body:"All officers report to designated muster point immediately." },
                { label:"End of Shift Report", type:"broadcast", title:"End of Shift Report Due", body:"Please complete and submit your daily occurrence log before leaving site." },
              ].map(q => (
                <button key={q.label} onClick={() => setForm(f=>({...f, type:q.type, title:q.title, body:q.body}))}
                  style={{ display:"block", width:"100%", textAlign:"left", background:"#F8F7F4", border:"1px solid #E2DDD6", borderRadius:7, padding:"10px 12px", marginBottom:8, cursor:"pointer", fontSize:12, color:"#1A1A2E", fontWeight:600 }}>
                  {q.label}
                  <span style={{ display:"block", fontSize:10, color:"#9B8F7A", fontWeight:400, marginTop:2 }}>{q.type}</span>
                </button>
              ))}
            </div>

            <div style={S.card}>
              <div style={{ fontSize:12, fontWeight:700, color:"#1A1A2E", marginBottom:10 }}>Officers Online</div>
              {officers.length === 0
                ? <div style={{ fontSize:12, color:"#9B8F7A" }}>No officers found.</div>
                : officers.map(o => (
                  <div key={o.uid} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 0", borderBottom:"1px solid #F4F0E8" }}>
                    <div style={{ width:28, height:28, borderRadius:7, background:"#1A1A2E", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:"#C9A84C", flexShrink:0 }}>
                      {(o.name||o.email||"?")[0].toUpperCase()}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:"#1A1A2E" }}>{o.name||o.email}</div>
                      <div style={{ fontSize:10, color:"#9B8F7A" }}>{o.role||"Officer"}</div>
                    </div>
                    {o.fcm_token && <div style={{ width:7, height:7, borderRadius:"50%", background:"#2E7D52" }} title="Has FCM token"/>}
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {tab === "history" && (
        <div style={S.card}>
          {notifications.length === 0
            ? <div style={{ textAlign:"center", padding:48, color:"#9B8F7A" }}>No notifications sent yet.</div>
            : notifications.map(n => {
              const nt = NOTIF_TYPES.find(t=>t.id===n.type) || NOTIF_TYPES[2];
              return (
                <div key={n.id} onClick={() => !n.read && markRead(n.id)} style={{
                  display:"flex", alignItems:"flex-start", gap:12, padding:"12px 0",
                  borderBottom:"1px solid #F4F0E8", cursor: n.read?"default":"pointer",
                  opacity: n.read ? 0.7 : 1
                }}>
                  <div style={{ width:36, height:36, borderRadius:8, background:nt.bg, border:`1px solid ${nt.color}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:nt.color }}/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div style={{ fontSize:13, fontWeight: n.read?600:800, color:"#1A1A2E" }}>{n.title}</div>
                      <div style={{ fontSize:10, color:"#9B8F7A", whiteSpace:"nowrap", marginLeft:8 }}>{fmtTime(n.sent_at)}</div>
                    </div>
                    <div style={{ fontSize:12, color:"#6B6055", marginTop:3 }}>{n.body}</div>
                    <div style={{ display:"flex", gap:8, marginTop:5, flexWrap:"wrap" }}>
                      <span style={{ background:nt.bg, color:nt.color, padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700 }}>{nt.label}</span>
                      {n.target_name && <span style={{ fontSize:10, color:"#9B8F7A" }}>To: {n.target_name}</span>}
                      {n.site && <span style={{ fontSize:10, color:"#9B8F7A" }}>Site: {n.site}</span>}
                      {n.sent_by && <span style={{ fontSize:10, color:"#9B8F7A" }}>By: {n.sent_by}</span>}
                      {!n.read && <span style={{ background:"#C0392B", color:"#fff", padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700 }}>Unread</span>}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      )}


      {/* RESPONSES TAB */}
      {tab === "responses" && (
        <div style={S.card}>
          <div style={{ fontSize:13, fontWeight:700, color:"#1A1A2E", marginBottom:16 }}>
            Officer Responses
            {unreadResponses > 0 && <span style={{ marginLeft:8, background:"#7C3AED18", color:"#7C3AED", padding:"2px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>{unreadResponses} new</span>}
          </div>
          {responses.length === 0 ? (
            <div style={{ textAlign:"center", padding:48, color:"#9B8F7A" }}>No officer responses yet.</div>
          ) : responses.map(r => (
            <div key={r.id} style={{
              display:"flex", gap:12, padding:"14px 0", borderBottom:"1px solid #F4F0E8",
              opacity: r.read_by_admin ? 0.7 : 1
            }}>
              <div style={{ width:38, height:38, borderRadius:10, background: r.read_by_admin ? "#F4F2EE" : "#F3F0FF", border:"1px solid #E2DDD6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:"#7C3AED", flexShrink:0 }}>
                {(r.officer_name||r.officer_email||"?")[0].toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                  <div>
                    <span style={{ fontSize:13, fontWeight:700, color:"#1A1A2E" }}>{r.officer_name || r.officer_email}</span>
                    <span style={{ fontSize:11, color:"#9B8F7A", marginLeft:8 }}>re: {r.notification_title}</span>
                  </div>
                  <span style={{ fontSize:10, color:"#9B8F7A" }}>{r.responded_at?.toDate ? r.responded_at.toDate().toLocaleString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : ""}</span>
                </div>
                <div style={{ background:"#F8F7F4", border:"1px solid #E2DDD6", borderRadius:8, padding:"8px 12px", fontSize:13, color:"#1A1A2E", marginBottom:8 }}>
                  {r.message}
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  {r.site && <span style={{ fontSize:11, color:"#9B8F7A" }}>Site: {r.site}</span>}
                  <span style={{ fontSize:9, fontWeight:800, letterSpacing:0.5, padding:"2px 8px", borderRadius:20, background: r.read_by_admin ? "#F0FFF4" : "#FFF8E7", color: r.read_by_admin ? "#2E7D52" : "#C9A84C" }}>
                    {r.read_by_admin ? "SEEN" : "NEW"}
                  </span>
                  {!r.read_by_admin && (
                    <button onClick={() => markResponseRead(r.id)} style={{ fontSize:10, color:"#9B8F7A", background:"none", border:"1px solid #E2DDD6", borderRadius:5, padding:"2px 8px", cursor:"pointer", fontWeight:600 }}>
                      Mark seen
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TEMPLATES TAB */}
      {tab === "templates" && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
          {[
            { title:"Morning Briefing", type:"broadcast", body:"Good morning. Please ensure all access points are secured and daily log is started. Report any anomalies immediately." },
            { title:"Patrol Due", type:"system", body:"Your patrol is due to start. Please scan all NFC points and complete the route within the allotted time." },
            { title:"Incident Escalation", type:"incident", body:"A Level 3 incident has been reported at your site. Implement emergency protocols and contact control room immediately." },
            { title:"Fire Test Today", type:"system", body:"Scheduled fire alarm test at 10:00. Please notify all occupants and ensure alarm receiving centre is isolated beforehand." },
            { title:"Shift Cover Needed", type:"shift", body:"We urgently require cover for a shift. Please contact the control room if you are available." },
            { title:"SIA Check Reminder", type:"system", body:"Please ensure your SIA licence is uploaded and up to date in the Document Vault. Expired licences must be renewed immediately." },
          ].map(t => {
            const nt = NOTIF_TYPES.find(x=>x.id===t.type)||NOTIF_TYPES[2];
            return (
              <div key={t.title} style={{ ...S.card, marginBottom:0, cursor:"pointer", borderLeft:`3px solid ${nt.color}` }}
                onClick={() => { setForm(f=>({...f, type:t.type, title:t.title, body:t.body})); setTab("send"); }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#1A1A2E" }}>{t.title}</div>
                  <span style={{ background:nt.bg, color:nt.color, padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700 }}>{nt.label}</span>
                </div>
                <div style={{ fontSize:12, color:"#6B6055", lineHeight:1.5 }}>{t.body}</div>
                <div style={{ marginTop:10, fontSize:11, color:nt.color, fontWeight:600 }}>Use template →</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
